import { Schema } from "effect";
import type { DomainEvent } from "./DomainEvent";

/**
 * Domain event published when an invoice PDF is successfully uploaded to remote storage.
 * This event confirms that the invoice document is safely backed up.
 * 
 * Business rules captured:
 * - PDF has been successfully uploaded to remote storage
 * - Storage location is recorded for future retrieval
 * - Upload metadata is captured for compliance and audit
 */
export interface InvoicePDFUploaded extends DomainEvent {
  readonly type: "InvoicePDFUploaded";
  readonly invoiceId: string;
  readonly invoiceNumber: string;
  readonly storageLocation: string;
  readonly storageProvider: string;
  readonly uploadedAt: Date;
  readonly fileSizeBytes: number;
  readonly checksumMD5: string;
  readonly contentType: "application/pdf";
  readonly uploadMetadata: {
    readonly retryAttempts: number;
    readonly uploadDurationMs: number;
  };
}

/**
 * Schema for InvoicePDFUploaded domain event
 */
export const InvoicePDFUploadedSchema = Schema.Struct({
  // Base domain event fields
  type: Schema.Literal("InvoicePDFUploaded"),
  eventId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Event ID must be a valid UUID",
      }
    )
  ),
  occurredOn: Schema.DateFromSelf,
  version: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(1, {
      message: () => "Event version must be a positive integer",
    })
  ),
  aggregateType: Schema.String.pipe(Schema.nonEmptyString()),
  aggregateId: Schema.String.pipe(Schema.nonEmptyString()),
  correlationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  causationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  
  // InvoicePDFUploaded specific fields
  invoiceId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Invoice ID must be a valid UUID",
      }
    )
  ),
  invoiceNumber: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(/^(INV-\d+|\d+)$/, {
      message: () => "Invoice number must follow format INV-1001 or 1221",
    })
  ),
  storageLocation: Schema.String.pipe(Schema.nonEmptyString()),
  storageProvider: Schema.String.pipe(Schema.nonEmptyString()),
  uploadedAt: Schema.DateFromSelf,
  fileSizeBytes: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThan(0, {
      message: () => "File size must be positive",
    })
  ),
  checksumMD5: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(/^[a-f0-9]{32}$/i, {
      message: () => "Checksum must be a valid MD5 hash",
    })
  ),
  contentType: Schema.Literal("application/pdf"),
  uploadMetadata: Schema.Struct({
    retryAttempts: Schema.Number.pipe(
      Schema.int(),
      Schema.greaterThanOrEqualTo(0, {
        message: () => "Retry attempts must be non-negative",
      })
    ),
    uploadDurationMs: Schema.Number.pipe(
      Schema.int(),
      Schema.greaterThan(0, {
        message: () => "Upload duration must be positive",
      })
    ),
  }),
});

/**
 * Schema for InvoicePDFUploadFailed domain event
 */
export const InvoicePDFUploadFailedSchema = Schema.Struct({
  // Base domain event fields
  type: Schema.Literal("InvoicePDFUploadFailed"),
  eventId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Event ID must be a valid UUID",
      }
    )
  ),
  occurredOn: Schema.DateFromSelf,
  version: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(1, {
      message: () => "Event version must be a positive integer",
    })
  ),
  aggregateType: Schema.String.pipe(Schema.nonEmptyString()),
  aggregateId: Schema.String.pipe(Schema.nonEmptyString()),
  correlationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  causationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  
  // InvoicePDFUploadFailed specific fields
  invoiceId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Invoice ID must be a valid UUID",
      }
    )
  ),
  invoiceNumber: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(/^(INV-\d+|\d+)$/, {
      message: () => "Invoice number must follow format INV-1001 or 1221",
    })
  ),
  failureReason: Schema.String.pipe(Schema.nonEmptyString()),
  failureCategory: Schema.Literal("network", "authentication", "storage", "validation"),
  httpStatusCode: Schema.optional(
    Schema.Number.pipe(
      Schema.int(),
      Schema.between(100, 599, {
        message: () => "HTTP status code must be between 100 and 599",
      })
    )
  ),
  retryable: Schema.Boolean,
  retryAttempt: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(0, {
      message: () => "Retry attempt must be non-negative",
    })
  ),
  maxRetries: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(0, {
      message: () => "Max retries must be non-negative",
    })
  ),
  nextRetryAt: Schema.optional(Schema.DateFromSelf),
  errorDetails: Schema.optional(
    Schema.Record({
      key: Schema.String,
      value: Schema.Unknown,
    })
  ),
});

/**
 * Type aliases for the events
 */
export type InvoicePDFUploadedType = Schema.Schema.Type<typeof InvoicePDFUploadedSchema>;
export type InvoicePDFUploadFailedType = Schema.Schema.Type<typeof InvoicePDFUploadFailedSchema>;
